import React, { useState, useRef, useEffect } from 'react'
import { Canvas } from '@react-three/fiber'
import { OrbitControls, Stats } from '@react-three/drei'
import { exportSTLString, stlLoader, STLLoadResult } from '../../utils/stlLoader'
import { useTabContext } from '../../contexts/TabContext'
import { logSTLError, logInfo, logError } from '../../utils/errorLogger'
import { backendService, StlFileRole } from '../../services/backendService'
import { getCachedSTL, cacheSTL } from '../../services/stlCache'
import { useStabilityManager } from '../../hooks/useStabilityManager'
import { debugLog, debugError } from '../../utils/debugLogger'
import type { DownloadProgress, UploadProgress } from '../GirasolesView/LoadingStatus'
import { LoadingStatus } from '../GirasolesView/LoadingStatus'
import { CalibrationWizard } from '../../components/CalibrationWizard'
import MeasurementsPanel from '../../components/MeasurementsPanel'
import { APP_VERSION } from '../../version'
import { computeLegacyOrientationData, OrientationData } from './hooks/useOrientationData'
import { CalibrationMarkers } from './components/CalibrationMarkers'
import { STLMesh } from './components/STLMesh'
import { OrientationArrows } from './components/OrientationArrows'
import type { STLFileMetadata, StlInStatusResponse } from '../../services/types/stl'
import './STLInView.css'

interface ViewerErrorBoundaryProps {
  children: React.ReactNode
  fallback: React.ReactNode
  onError?: (error: Error) => void
  resetKey: string
}

interface ViewerErrorBoundaryState {
  hasError: boolean
}

class ViewerErrorBoundary extends React.Component<ViewerErrorBoundaryProps, ViewerErrorBoundaryState> {
  state: ViewerErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error) {
    this.props.onError?.(error)
  }

  componentDidUpdate(prevProps: ViewerErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false })
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback
    }

    return this.props.children
  }
}

interface STLInViewProps {
  projectId?: string
  projectName?: string
  customerName?: string
  fileRole?: StlFileRole
  refreshKey?: string
}

export const STLInView: React.FC<STLInViewProps> = ({
  projectId,
  projectName,
  customerName = 'Unknown Patient',
  fileRole = 'stl_in',
  refreshKey
}) => {
  const isOutputView = fileRole === 'stl_out'
  const tabContext = useTabContext()

  // Stability manager for preventing race conditions and managing loading states
  const stability = useStabilityManager('STL_IN', {
    debounceMs: 500,
    requestTtl: 180000, // 3 minutes for STL operations
    clickCooldown: 1000,
    maxConcurrentRequests: 2
  })

  const [stlData, setStlData] = useState<STLLoadResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showWireframe, setShowWireframe] = useState(false)
  const [showStats, setShowStats] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState<string | null>(null)
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null)
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null)
  const [originalBoundingBox, setOriginalBoundingBox] = useState<{ min: { x: number; y: number; z: number }; max: { x: number; y: number; z: number }; size: { x: number; y: number; z: number } } | null>(null)
  const [serverMetadata, setServerMetadata] = useState<STLFileMetadata | null>(null)
  const [serverProcessingStatus, setServerProcessingStatus] = useState<string | null>(null)
  const [viewerError, setViewerError] = useState<string | null>(null)
  const [isSavingSTL, setIsSavingSTL] = useState(false)
  const [isSavingCalibration, setIsSavingCalibration] = useState(false)
  const [fileId, _setFileId] = useState<string | null>(null)
  const fileIdRef = useRef<string | null>(null)
  const setFileId = (id: string | null) => { fileIdRef.current = id; _setFileId(id) }
  const [originalUploadedFile, setOriginalUploadedFile] = useState<File | null>(null)

  // Calibration state
  const [calibrationMode, setCalibrationMode] = useState(false)
  const [calibrationStep, setCalibrationStep] = useState(0) // 0=heel, 1=MT1, 2=MT5
  const [calibrationPoints, setCalibrationPoints] = useState<{
    heel?: { x: number; y: number; z: number }
    mt1?: { x: number; y: number; z: number }
    mt5?: { x: number; y: number; z: number }
  }>({})

  // Info panel resize state
  const DEFAULT_PANEL_WIDTH = 300
  const MIN_PANEL_WIDTH = 200
  const MAX_PANEL_WIDTH = 500
  const PANEL_WIDTH_STORAGE_KEY = 'stlin-info-panel-width'
  const [infoPanelWidth, setInfoPanelWidth] = useState<number>(() => {
    const saved = localStorage.getItem(PANEL_WIDTH_STORAGE_KEY)
    return saved ? parseInt(saved, 10) : DEFAULT_PANEL_WIDTH
  })
  const [isResizingPanel, setIsResizingPanel] = useState(false)
  const resizeStartX = useRef<number>(0)
  const resizeStartWidth = useRef<number>(0)
  const currentResizeWidth = useRef<number>(DEFAULT_PANEL_WIDTH)

  const handlePanelResizeStart = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizingPanel(true)
    resizeStartX.current = e.clientX
    resizeStartWidth.current = infoPanelWidth
    currentResizeWidth.current = infoPanelWidth
  }

  useEffect(() => {
    if (!isResizingPanel) return
    document.body.classList.add('resizing-panel')
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartX.current - e.clientX
      let newWidth = resizeStartWidth.current + deltaX
      newWidth = Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, newWidth))
      currentResizeWidth.current = newWidth
      setInfoPanelWidth(newWidth)
    }
    const handleMouseUp = () => {
      setIsResizingPanel(false)
      document.body.classList.remove('resizing-panel')
      localStorage.setItem(PANEL_WIDTH_STORAGE_KEY, currentResizeWidth.current.toString())
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.classList.remove('resizing-panel')
    }
  }, [isResizingPanel])

  // Measurements state
  const [measurements, setMeasurements] = useState<{
    foot_length?: number
    foot_width?: number
    arch_height?: number
    arch_classification?: string
    heel_width?: number
    instep_height?: number
    units?: string
  } | null>(null)
  const [measurementsLoading, setMeasurementsLoading] = useState(false)
  const [orientationData, setOrientationData] = useState<OrientationData | null>(null)
  const [invertFrontBackView, setInvertFrontBackView] = useState(false)
  const [invertTopBottomView, setInvertTopBottomView] = useState(false)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const isLoadedRef = useRef(false) // Track if STL has been loaded successfully
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    setViewerError(null)
  }, [projectId, fileRole, refreshKey])

  useEffect(() => {
    let cancelled = false

    if (!fileId) {
      setServerMetadata(null)
      setServerProcessingStatus(null)
      return
    }

    void (async () => {
      try {
        const metadataResponse = await backendService.getSTLMetadata(fileId)
        if (!cancelled) {
          setServerMetadata(metadataResponse.metadata)
          setServerProcessingStatus(metadataResponse.processingStatus ?? null)
        }
      } catch (metadataError) {
        const message = metadataError instanceof Error ? metadataError.message : 'Failed to fetch STL metadata'
        logError(`Failed to fetch STL metadata for ${fileId}: ${message}`, 'STL_IN')
        if (!cancelled) {
          setServerMetadata(null)
          setServerProcessingStatus(null)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [fileId])

  // Validate projectId is a valid UUID format
  const isValidUUID = (id: string | undefined): boolean => {
    if (!id) return false
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    return uuidRegex.test(id)
  }

  // Load STL file on component mount - only if valid projectId
  useEffect(() => {
    const mountId = `mount-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    debugLog(`useEffect triggered, isLoadedRef.current: ${isLoadedRef.current}`)
    logInfo(`🚀 [${mountId}] STL_IN Component mounted/updated`, 'STL_IN')
    logInfo(`🚀 [${mountId}] Props:`, 'STL_IN')
    logInfo(`🚀 [${mountId}] - projectId: ${projectId}`, 'STL_IN')
    logInfo(`🚀 [${mountId}] - customerName: ${customerName}`, 'STL_IN')

    // Force a reload when refreshKey is provided/changed.
    // (Must happen before the "already loaded" early return.)
    if (refreshKey !== undefined) {
      isLoadedRef.current = false
    }

    // Prevent duplicate loads - only load once (unless refreshKey forces reload).
    if (isLoadedRef.current) {
      debugLog('STL already loaded, skipping duplicate call')
      logInfo(`🚀 [${mountId}] STL already loaded, skipping duplicate call`, 'STL_IN')
      return
    }

    if (!projectId) {
      const errorMsg = 'No project ID provided. STL_IN requires a valid project UUID.'
      logError(`🚀 [${mountId}] ${errorMsg}`, 'STL_IN')
      setError(errorMsg)
      return
    }

    if (!isValidUUID(projectId)) {
      const errorMsg = `Invalid project ID format: "${projectId}". Expected a valid UUID.`
      logError(`🚀 [${mountId}] ${errorMsg}`, 'STL_IN')
      setError(errorMsg)
      return
    }

    debugLog('Starting STL load')
    logInfo(`🚀 [${mountId}] Loading existing STL for project: ${projectId}`, 'STL_IN')
    loadExistingSTL()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, refreshKey])

  const handleCancelDownload = () => {
    if (abortControllerRef.current) {
      debugLog('Cancelling download...')
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      setDownloadProgress(null)
      setLoading(false)
      setError('Download cancelled')
      isLoadedRef.current = false
    }
  }

  const loadExistingSTL = async () => {
    debugLog('loadExistingSTL called')
    debugLog('projectId:', projectId)
    debugLog('isLoadedRef.current:', isLoadedRef.current)

    // Check if already loaded or loading
    if (isLoadedRef.current) {
      debugLog('Already loaded, skipping')
      return
    }

    // Mark as loading immediately to prevent duplicate calls
    debugLog('Setting isLoadedRef.current = true to prevent duplicates')
    isLoadedRef.current = true

    if (!projectId) {
      debugError('No projectId - aborting')
      logError(`Cannot load STL - no projectId provided`, 'STL_IN')
      isLoadedRef.current = false
      return
    }

    const requestId = `load-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    debugLog(`requestId: ${requestId}`)
    logInfo(`🔍 [${requestId}] LOAD OPERATION STARTED - Project: ${projectId}, Customer: ${customerName}`, 'STL_IN')

    debugLog('Setting loading=true')
    setLoading(true)
    setError(null)
    debugLog('Loading state set')

    // Retry configuration
    const MAX_RETRIES = 3
    let attempt = 0

    debugLog('Starting retry loop, MAX_RETRIES:', MAX_RETRIES)

    while (attempt < MAX_RETRIES) {
      let stlInStatus: StlInStatusResponse | undefined

      try {
        attempt++
        debugLog(`Attempt ${attempt}/${MAX_RETRIES} - Starting`)
        logInfo(`📡 [${requestId}] Attempt ${attempt}/${MAX_RETRIES} - Step 1: Checking STL status via getStlStatus(${projectId}, ${fileRole})`, 'STL_IN')
        const statusStartTime = Date.now()

        debugLog('Calling backendService.getStlStatus...')
        stlInStatus = await backendService.getStlStatus(projectId, fileRole)
        debugLog('getStlStatus returned:', stlInStatus)
        const statusDuration = Date.now() - statusStartTime

      logInfo(`📡 [${requestId}] Step 1 RESPONSE (${statusDuration}ms):`, 'STL_IN')
      logInfo(`📡 [${requestId}] - exists: ${stlInStatus.exists}`, 'STL_IN')
      logInfo(`📡 [${requestId}] - latest: ${JSON.stringify(stlInStatus.latest, null, 2)}`, 'STL_IN')

      if (!stlInStatus.exists || !stlInStatus.latest) {
        logInfo(`❌ [${requestId}] No existing STL file found - exists: ${stlInStatus.exists}, latest: ${!!stlInStatus.latest}`, 'STL_IN')
        // Clear ALL stale state so the empty-state UI renders properly.
        // Without this, stale stlData from a previous mount persists — the 3D
        // canvas shows blank while handleUploadClick's confirm("replace?") falsely
        // fires because stlData is still truthy.
        setFileId(null)
        setStlData(null)
        setViewerError(null)
        setOriginalUploadedFile(null)
        setCalibrationMode(false)
        setCalibrationStep(0)
        setCalibrationPoints({})
        setMeasurements(null)
        setServerMetadata(null)
        setServerProcessingStatus(null)
        setOriginalBoundingBox(null)
        setLoading(false)
        isLoadedRef.current = false
        return
      }

      // Step 2: Prepare download
      logInfo(`📥 [${requestId}] Step 2: Preparing STL download`, 'STL_IN')
      logInfo(`📥 [${requestId}] - File: ${stlInStatus.latest.filename}`, 'STL_IN')
      logInfo(`📥 [${requestId}] - Size: ${stlInStatus.latest.sizeBytes} bytes (${(stlInStatus.latest.sizeBytes / (1024 * 1024)).toFixed(2)} MB)`, 'STL_IN')
      logInfo(`📥 [${requestId}] - Vertices: ${stlInStatus.latest.vertices}`, 'STL_IN')
      logInfo(`📥 [${requestId}] - Faces: ${stlInStatus.latest.faces}`, 'STL_IN')

      const fileSizeMB = stlInStatus.latest.sizeBytes / (1024 * 1024)

      // Step 2b: Check IndexedDB cache
      const cachedBuffer = await getCachedSTL(projectId, fileRole, {
        fileId: stlInStatus.latest.fileId,
        uploadedAt: stlInStatus.latest.uploadedAt,
      })

      let arrayBuffer: ArrayBuffer

      if (cachedBuffer) {
        logInfo(`⚡ [${requestId}] STL cache HIT — skipping download (${fileSizeMB.toFixed(2)} MB)`, 'STL_IN')
        arrayBuffer = cachedBuffer
      } else {
        logInfo(`📥 [${requestId}] STL cache MISS — downloading ${fileSizeMB.toFixed(2)} MB`, 'STL_IN')

      // Step 3: Single streaming download with real progress
      debugLog(`Step 3: Starting streaming download (${fileSizeMB.toFixed(2)} MB)`)
      logInfo(`📥 [${requestId}] Step 3: Streaming download for ${fileSizeMB.toFixed(2)} MB file`, 'STL_IN')

      const downloadUrl = backendService.getSTLDownloadUrl(projectId, fileRole)
      const authToken = backendService.getAuthToken()
      const headers: { [key: string]: string } = {}
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`
      }

      abortControllerRef.current = new AbortController()
      const downloadStartTime = Date.now()

      // Step 3a: Fetch download info — backend may return a pre-signed R2 URL (JSON)
      //          or stream the file directly (fallback for local dev / no R2 configured).
      const infoResponse = await fetch(downloadUrl, { headers, signal: abortControllerRef.current.signal, credentials: 'include' })
      if (!infoResponse.ok) {
        throw new Error(`HTTP ${infoResponse.status}: ${infoResponse.statusText}`)
      }

      let fileResponse: Response
      const contentType = infoResponse.headers.get('Content-Type') ?? ''
      if (contentType.includes('application/json')) {
        // Backend returned { url: "<presigned R2 URL>" } — fetch directly from R2
        const { url: r2Url } = await infoResponse.json() as { url: string }
        logInfo(`📥 [${requestId}] Using R2 pre-signed URL for fast CDN download`, 'STL_IN')
        fileResponse = await fetch(r2Url, { signal: abortControllerRef.current.signal })
        if (!fileResponse.ok) {
          throw new Error(`R2 HTTP ${fileResponse.status}: ${fileResponse.statusText}`)
        }
      } else {
        // Backend streamed the file directly (local dev fallback)
        fileResponse = infoResponse
      }

      const contentLength = fileResponse.headers.get('Content-Length')
      const totalBytes = contentLength ? parseInt(contentLength) : stlInStatus.latest.sizeBytes
      const reader = fileResponse.body!.getReader()
      const receivedChunks: Uint8Array[] = []
      let bytesDownloaded = 0

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        receivedChunks.push(value)
        bytesDownloaded += value.byteLength
        const elapsed = Math.max((Date.now() - downloadStartTime) / 1000, 0.001)
        const speed = bytesDownloaded / elapsed
        const remaining = (totalBytes - bytesDownloaded) / speed
        setDownloadProgress({
          stage: 'downloading',
          percentage: Math.min(99, (bytesDownloaded / totalBytes) * 100),
          bytesDownloaded,
          totalBytes,
          downloadSpeed: speed,
          estimatedTimeRemaining: remaining
        })
      }

      // Assemble received chunks into a single ArrayBuffer
      const totalLength = receivedChunks.reduce((sum, c) => sum + c.byteLength, 0)
      const assembled = new Uint8Array(totalLength)
      let offset = 0
      for (const chunk of receivedChunks) {
        assembled.set(chunk, offset)
        offset += chunk.byteLength
      }
      arrayBuffer = assembled.buffer

      const downloadDuration = Date.now() - downloadStartTime
      logInfo(`📥 [${requestId}] Streaming download completed (${downloadDuration}ms, ${totalLength} bytes)`, 'STL_IN')
      setDownloadProgress(null)
      abortControllerRef.current = null

      // Cache the downloaded STL in IndexedDB for next time
      cacheSTL(projectId, fileRole, {
        fileId: stlInStatus.latest.fileId,
        uploadedAt: stlInStatus.latest.uploadedAt,
        filename: stlInStatus.latest.filename,
        sizeBytes: stlInStatus.latest.sizeBytes,
      }, arrayBuffer).catch(() => {/* best-effort */})

      } // end cache-miss else block

      // Step 4: Parse STL
      debugLog('Step 4: Starting STL parsing')
      logInfo(`🔧 [${requestId}] Step 4: Parsing STL data`, 'STL_IN')

      const parseStartTime = Date.now()

      debugLog('Calling stlLoader.loadSTLFromArrayBuffer...')
      const result = await stlLoader.loadSTLFromArrayBuffer(arrayBuffer)
      debugLog('STL parsing completed, result:', result)
      const parseDuration = Date.now() - parseStartTime

      logInfo(`🔧 [${requestId}] STL parsed (${parseDuration}ms):`, 'STL_IN')
      logInfo(`🔧 [${requestId}] - Vertices: ${result.metadata.vertices}`, 'STL_IN')
      logInfo(`🔧 [${requestId}] - Faces: ${result.metadata.faces}`, 'STL_IN')
      logInfo(`🔧 [${requestId}] - Bounding Box: ${JSON.stringify({min: result.metadata.boundingBox.min, max: result.metadata.boundingBox.max})}`, 'STL_IN')
      logInfo(`🔧 [${requestId}] - Size: ${JSON.stringify(result.metadata.size)}`, 'STL_IN')
      logInfo(`🔧 [${requestId}] - Center: ${JSON.stringify(result.metadata.center)}`, 'STL_IN')

      // Step 5: Normalize geometry and render
      debugLog('Step 5: Normalizing geometry and rendering')
      logInfo(`🔧 [${requestId}] Step 5: Normalizing geometry and rendering`, 'STL_IN')

      const scaleFactor = stlLoader.normalizeGeometry(result.geometry, 8)
      // Persist the normalization scale factor so we can:
      // 1) export in the correct backend coordinate space, and
      // 2) denormalize calibration points consistently across reloads.
      result.scaleFactor = scaleFactor
      result.geometry.computeVertexNormals()
      result.geometry.normalizeNormals()
      debugLog('Normalization completed')

        // Step 6: Fetch bounding box from backend database
        debugLog('Step 6: Fetching bounding box from backend')
        try {
          const metadataUrl = `${backendService.getBaseURL()}/api/stl/${stlInStatus.latest.fileId}`
          const authToken = backendService.getAuthToken()
          const metadataResponse = await fetch(metadataUrl, {
            headers: authToken ? { 'Authorization': `Bearer ${authToken}` } : {},
            credentials: 'include',
          })

          if (metadataResponse.ok) {
            const metadataData = await metadataResponse.json()

            // Prefer originalBoundingBox (real-world dimensions) over normalized boundingBox
            if (metadataData.metadata && metadataData.metadata.originalBoundingBox) {
              const origBbox = metadataData.metadata.originalBoundingBox
              const origBboxWithSize = {
                ...origBbox,
                size: origBbox.size || {
                  x: origBbox.max.x - origBbox.min.x,
                  y: origBbox.max.y - origBbox.min.y,
                  z: origBbox.max.z - origBbox.min.z
                }
              }
              debugLog('Original bounding box fetched from backend:', origBboxWithSize)
            } else if (metadataData.metadata && metadataData.metadata.boundingBox) {
              // Fallback to normalized bounding box if original not available
              const bbox = metadataData.metadata.boundingBox
              const bboxWithSize = {
                ...bbox,
                size: bbox.size || {
                  x: bbox.max.x - bbox.min.x,
                  y: bbox.max.y - bbox.min.y,
                  z: bbox.max.z - bbox.min.z
                }
              }
              debugLog('Normalized bounding box fetched from backend (original not available):', bboxWithSize)
            }
          }
        } catch (err) {
          debugError('Failed to fetch backend bounding box:', err)
          // Non-critical error - continue without backend bounding box
        }

        // Step 7: Set data and complete
        debugLog('Step 7: Setting STL data in state')
        setStlData(result)
        setFileId(stlInStatus.latest.fileId ?? null)
        // This mesh came from backend download, not a fresh local import.
        // Keep this null so a later "Save STL" falls back to export flow.
        setOriginalUploadedFile(null)
        debugLog('STL data set successfully')

        // Clear progress
        setDownloadProgress(null)

        const totalDuration = Date.now() - parseInt(requestId.split('-')[1])
        logInfo(`✅ [${requestId}] LOAD OPERATION COMPLETED (${totalDuration}ms total)`, 'STL_IN')
        logInfo(`✅ [${requestId}] STL file loaded and displayed: ${stlInStatus.latest.filename}`, 'STL_IN')

        // Step 8: Fetch measurements if calibrated
        if (stlInStatus.latest.fileId) {
          debugLog('Step 8: Fetching measurements for calibrated STL')
          await fetchMeasurements(stlInStatus.latest.fileId)
        }

        // Success - break retry loop (isLoadedRef stays true)
        debugLog('Breaking retry loop - success!')
        break

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error'
        debugError('Error caught:', errorMessage)
        logError(`❌ [${requestId}] Attempt ${attempt}/${MAX_RETRIES} FAILED: ${errorMessage}`, 'STL_IN')
        logError(`❌ [${requestId}] Error details: ${err instanceof Error ? err.stack : 'No stack trace'}`, 'STL_IN')

        // Clear progress on error
        setDownloadProgress(null)
        abortControllerRef.current = null

        // Check if this is a cancellation
        if (errorMessage.includes('cancelled by user') || errorMessage.includes('aborted')) {
          debugLog('Download cancelled by user')
          logInfo(`⚠️ [${requestId}] Download cancelled by user`, 'STL_IN')
          setError('Download cancelled')
          isLoadedRef.current = false
          break
        }

        // Check if this is a non-retryable error
        const isAuthError = errorMessage.includes('401') ||
                            errorMessage.includes('403') ||
                            errorMessage.includes('token') ||
                            errorMessage.includes('authentication')
        const isNotFound = errorMessage.includes('404')
        const isNonRetryable = isAuthError || isNotFound

        if (isNonRetryable) {
          debugError('Non-retryable error detected')
          logError(`❌ [${requestId}] Non-retryable error detected, stopping attempts`, 'STL_IN')
          // Distinguish "no file ever uploaded" (404) from auth errors
          // and "file record exists but download failed" (R2 / disk issues).
          let userMessage: string
          if (isNotFound) {
            // The stlInStatus variable is only available if Step 1 completed
            // successfully. If it did and exists=true, the 404 came from the
            // download stage — the file record exists but can't be fetched
            // (common on production when R2 fix isn't deployed).
            const statusWasFound = typeof stlInStatus !== 'undefined' && stlInStatus.exists
            userMessage = statusWasFound
              ? 'STL file record found but download failed. The file may be missing from storage. Please try re-uploading the STL file.'
              : 'No STL file uploaded for this project yet. Use the Upload button above to add a foot scan.'
          } else {
            userMessage = `Authentication error: ${errorMessage}`
          }
          setError(userMessage)
          logSTLError('load_existing', 'existing_stl', errorMessage, { projectId, customerName, requestId, nonRetryable: true })
          setFileId(null)
          isLoadedRef.current = false // Allow retry on next mount
          break
        }

        if (attempt >= MAX_RETRIES) {
          debugError('All retry attempts exhausted')
          logError(`❌ [${requestId}] All retry attempts exhausted (${MAX_RETRIES} total attempts)`, 'STL_IN')

          // Provide user-friendly error messages
          let userFriendlyMessage = `Failed to load STL file: ${errorMessage}`
          if (errorMessage.includes('500')) {
            userFriendlyMessage = 'Server error: The backend encountered an issue. Please try refreshing the page or contact support.'
          } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
            userFriendlyMessage = 'Network error: Please check your internet connection and try again.'
          } else if (errorMessage.includes('timeout')) {
            userFriendlyMessage = 'Request timeout: The server is taking too long to respond. Please try again.'
          }

          setError(userFriendlyMessage)
          logSTLError('load_existing', 'existing_stl', errorMessage, { projectId, customerName, requestId, attempts: attempt })
          isLoadedRef.current = false // Allow retry on next mount
          break
        }

        // Wait before retry (exponential backoff)
        const waitTime = Math.pow(2, attempt - 1) * 1000 // 1s, 2s, 4s
        logInfo(`⏳ [${requestId}] Waiting ${waitTime/1000}s before retry attempt ${attempt + 1}...`, 'STL_IN')
        await new Promise(resolve => setTimeout(resolve, waitTime))
      }
    }

    debugLog('Setting loading=false')
    setLoading(false)
    debugLog('Loading state cleared')
    logInfo(`🏁 [${requestId}] Load operation cleanup completed`, 'STL_IN')
    debugLog('loadExistingSTL function completed')
  }

  const resolveExistingFileId = async (): Promise<string | null> => {
    if (fileIdRef.current) {
      return fileIdRef.current
    }

    if (!projectId) {
      return null
    }

    logInfo('Resolving existing STL file ID for calibration...', 'STL_IN')
    const stlStatus = await backendService.getStlStatus(projectId, fileRole)
    const existingFileId = stlStatus.latest?.fileId ?? null

    if (existingFileId) {
      setFileId(existingFileId)
      logInfo(`Resolved existing STL file ID: ${existingFileId}`, 'STL_IN')
    }

    return existingFileId
  }

  const handleUploadClick = () => {
    // Clear any lingering error before starting a new upload
    setError(null)
    setViewerError(null)

    if (stlData) {
      const confirmed = window.confirm(
        'An object is already loaded. Do you want to replace it with the new one?'
      )
      if (!confirmed) return
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.name.toLowerCase().endsWith('.stl')) {
      setError('Please select a valid STL file')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Clean up existing STL data if present
      if (stlData) {
        try {
          stlData.geometry.dispose()
          const material: any = (stlData.mesh as any)?.material
          if (material && typeof material.dispose === 'function') material.dispose()
        } catch (_) { /* ignore cleanup errors */ }
      }

      logInfo(`Loading STL file from upload: ${file.name}`, 'STL_IN')
      const result = await stlLoader.loadSTLFromFile(file)
      setOriginalUploadedFile(file)

      // CRITICAL: Capture original bounding box BEFORE normalization
      // This preserves the real-world dimensions (e.g., 250mm x 230mm x 40mm)
      const originalBBox = result.metadata.boundingBox
      const originalSize = result.metadata.size
      const capturedOriginalBoundingBox = {
        min: { x: originalBBox.min.x, y: originalBBox.min.y, z: originalBBox.min.z },
        max: { x: originalBBox.max.x, y: originalBBox.max.y, z: originalBBox.max.z },
        size: { x: originalSize.x, y: originalSize.y, z: originalSize.z }
      }
      setOriginalBoundingBox(capturedOriginalBoundingBox)
      logInfo(`Original bounding box captured (before normalization): ${JSON.stringify(capturedOriginalBoundingBox.size)}`, 'STL_IN')

      // Normalize the geometry for better viewing
      const scaleFactor = stlLoader.normalizeGeometry(result.geometry, 8)
      result.scaleFactor = scaleFactor
      result.geometry.computeVertexNormals()
      result.geometry.normalizeNormals()

      setFileId(null)
      setStlData(result)
      logInfo(`STL uploaded and loaded successfully: ${file.name} (${result.metadata.vertices} vertices)`, 'STL_IN')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      setError(`Failed to load uploaded STL file: ${errorMessage}`)
      logSTLError('upload', file.name, errorMessage, { projectId, customerName, fileSize: file.size })
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSTL = stability.safeClick(
    'save-stl',
    async () => {
      if (!stlData) {
        logError('❌ SAVE OPERATION ABORTED: No STL data available', 'STL_IN')
        return
      }

      const requestId = `save-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      logInfo(`💾 [${requestId}] SAVE OPERATION STARTED - Project: ${projectId}, Customer: ${customerName}`, 'STL_IN')
      logInfo(`💾 [${requestId}] STL Data Summary:`, 'STL_IN')
      logInfo(`💾 [${requestId}] - Vertices: ${stlData.metadata.vertices}`, 'STL_IN')
      logInfo(`💾 [${requestId}] - Faces: ${stlData.metadata.faces}`, 'STL_IN')
      logInfo(`💾 [${requestId}] - Bounding Box: ${JSON.stringify({min: stlData.metadata.boundingBox.min, max: stlData.metadata.boundingBox.max})}`, 'STL_IN')
      logInfo(`💾 [${requestId}] - Size: ${JSON.stringify(stlData.metadata.size)}`, 'STL_IN')
      logInfo(`💾 [${requestId}] - Center: ${JSON.stringify(stlData.metadata.center)}`, 'STL_IN')

    setSaving(true)
    setIsSavingSTL(true)
    setSaveMessage(null)
    setUploadProgress(null)

    // Safety timeout to prevent stuck "Saving..." state
    const safetyTimeout = setTimeout(() => {
      logError(`⏰ [${requestId}] Safety timeout triggered - resetting save state`, 'STL_IN')
      setSaving(false)
      setSaveMessage('Save operation timed out. Please try again.')
    }, 30000) // 30 seconds safety timeout — was 180s, long enough to block the whole UI via LoadingStatus

    try {
      // Step 1: Prepare file payload
      // For STL_IN, prefer original imported bytes to preserve mesh definition exactly.
      let file: File
      if (!isOutputView && originalUploadedFile) {
        logInfo(`🔧 [${requestId}] Step 1: Using original uploaded STL bytes (no re-export)`, 'STL_IN')
        file = originalUploadedFile
      } else {
        logInfo(`🔧 [${requestId}] Step 1: Converting geometry to STL string`, 'STL_IN')
        const conversionStartTime = Date.now()

        const stlString = exportSTLString(stlData.geometry, stlData.scaleFactor)
        const conversionDuration = Date.now() - conversionStartTime

        logInfo(`🔧 [${requestId}] Conversion completed (${conversionDuration}ms):`, 'STL_IN')
        logInfo(`🔧 [${requestId}] - STL string length: ${stlString.length} characters`, 'STL_IN')
        logInfo(`🔧 [${requestId}] - STL string preview: ${stlString.substring(0, 200)}...`, 'STL_IN')

        // Step 2: Create file blob
        logInfo(`📦 [${requestId}] Step 2: Creating file blob`, 'STL_IN')
        const blob = new Blob([stlString], { type: 'application/sla' })
        const fileName = `${customerName}_STL_IN.stl`
        file = new File([blob], fileName, { type: 'application/sla' })
      }

      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2)
      logInfo(`📦 [${requestId}] File created:`, 'STL_IN')
      logInfo(`📦 [${requestId}] - Name: ${file.name}`, 'STL_IN')
      logInfo(`📦 [${requestId}] - Size: ${file.size} bytes (${fileSizeMB} MB)`, 'STL_IN')
      logInfo(`📦 [${requestId}] - Type: ${file.type}`, 'STL_IN')
      logInfo(`📦 [${requestId}] - Last Modified: ${new Date(file.lastModified).toISOString()}`, 'STL_IN')

      // Step 3: Prepare upload parameters
      logInfo(`📡 [${requestId}] Step 3: Preparing upload parameters`, 'STL_IN')
      logInfo(`📡 [${requestId}] - Project ID: ${projectId}`, 'STL_IN')
      logInfo(`📡 [${requestId}] - Patient ID: undefined`, 'STL_IN')
      logInfo(`📡 [${requestId}] - Doctor ID: undefined`, 'STL_IN')

      // Step 4: Upload with retry logic
      logInfo(`📤 [${requestId}] Step 4: Starting upload with retry logic`, 'STL_IN')
      let result
      let retryCount = 0
      const maxRetries = 2

      while (retryCount <= maxRetries) {
        const attemptId = `${requestId}-attempt-${retryCount + 1}`
        logInfo(`📤 [${attemptId}] Upload attempt ${retryCount + 1}/${maxRetries + 1}`, 'STL_IN')

        try {
          const uploadStartTime = Date.now()
          result = await backendService.uploadSTLFileWithProgress(
            file,
            projectId,
            undefined,
            undefined,
            originalBoundingBox || undefined,
            fileRole,
            ({ percentage, bytesUploaded, totalBytes }) => {
              const now = Date.now()
              const elapsed = (now - uploadStartTime) / 1000 // seconds
              const uploadSpeed = elapsed > 0 ? bytesUploaded / elapsed : 0
              const remainingBytes = totalBytes - bytesUploaded
              const estimatedTimeRemaining = uploadSpeed > 0 ? remainingBytes / uploadSpeed : 0
              setUploadProgress({ stage: 'uploading', percentage, bytesUploaded, totalBytes, uploadSpeed, estimatedTimeRemaining })
            }
          )
          const uploadDuration = Date.now() - uploadStartTime

          logInfo(`✅ [${attemptId}] Upload SUCCESS (${uploadDuration}ms):`, 'STL_IN')
          logInfo(`✅ [${attemptId}] - File ID: ${result?.fileId}`, 'STL_IN')
          logInfo(`✅ [${attemptId}] - Original Name: ${result?.originalName}`, 'STL_IN')
          logInfo(`✅ [${attemptId}] - Upload Date: ${result?.uploadDate}`, 'STL_IN')
          logInfo(`✅ [${attemptId}] - Success: ${result?.success}`, 'STL_IN')
          logInfo(`✅ [${attemptId}] - Processing Status: ${result?.processingStatus || 'No status'}`, 'STL_IN')
          logInfo(`✅ [${attemptId}] - Metadata: ${JSON.stringify(result?.metadata, null, 2)}`, 'STL_IN')

          break // Success, exit retry loop
        } catch (uploadError) {
          retryCount++
          const errorMessage = uploadError instanceof Error ? uploadError.message : 'Unknown upload error'

          logError(`❌ [${attemptId}] Upload FAILED: ${errorMessage}`, 'STL_IN')
          logError(`❌ [${attemptId}] Error details: ${uploadError instanceof Error ? uploadError.stack : 'No stack trace'}`, 'STL_IN')

          // Don't retry for certain error types that won't be resolved by retrying
          if (errorMessage.includes('409') || errorMessage.includes('401') || errorMessage.includes('403')) {
            logError(`❌ [${requestId}] Non-retryable error detected, stopping retry attempts`, 'STL_IN')
            throw uploadError // Don't retry for conflicts or auth errors
          }

          if (retryCount > maxRetries) {
            logError(`❌ [${requestId}] All retry attempts exhausted (${maxRetries + 1} total attempts)`, 'STL_IN')
            throw uploadError // Re-throw the error after max retries
          }

          // Wait before retry (exponential backoff)
          const waitTime = Math.pow(2, retryCount) * 1000 // 2s, 4s
          logInfo(`⏳ [${requestId}] Waiting ${waitTime/1000}s before retry attempt ${retryCount + 1}...`, 'STL_IN')
          await new Promise(resolve => setTimeout(resolve, waitTime))
        }
      }

      // Step 5: Success handling
      clearTimeout(safetyTimeout)
      setSaveMessage('✅ STL saved successfully!')
      const totalDuration = Date.now() - parseInt(requestId.split('-')[1])
      logInfo(`✅ [${requestId}] SAVE OPERATION COMPLETED (${totalDuration}ms total)`, 'STL_IN')
      logInfo(`✅ [${requestId}] Final result: ${JSON.stringify(result, null, 2)}`, 'STL_IN')

      // Capture fileId for later use (e.g., calibration)
      if (result?.fileId) {
        setFileId(result.fileId)
        logInfo(`✅ [${requestId}] File ID captured: ${result.fileId}`, 'STL_IN')
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSaveMessage(null), 3000)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'

      logError(`❌ [${requestId}] SAVE OPERATION FAILED: ${errorMessage}`, 'STL_IN')
      logError(`❌ [${requestId}] Error type: ${err?.constructor?.name || 'Unknown'}`, 'STL_IN')
      logError(`❌ [${requestId}] Error stack: ${err instanceof Error ? err.stack : 'No stack trace'}`, 'STL_IN')

      // Log additional context for debugging
      logError(`❌ [${requestId}] Context at failure:`, 'STL_IN')
      logError(`❌ [${requestId}] - Project ID: ${projectId}`, 'STL_IN')
      logError(`❌ [${requestId}] - Customer: ${customerName}`, 'STL_IN')
      logError(`❌ [${requestId}] - STL Data present: ${!!stlData}`, 'STL_IN')
      if (stlData) {
        logError(`❌ [${requestId}] - STL vertices: ${stlData.metadata.vertices}`, 'STL_IN')
        logError(`❌ [${requestId}] - STL faces: ${stlData.metadata.faces}`, 'STL_IN')
      }

      // Provide more specific error messages for common issues
      let userFriendlyMessage = `Failed to save STL: ${errorMessage}`
      if (errorMessage.includes('timeout')) {
        userFriendlyMessage = 'Upload timeout: The STL file is too large or the connection is slow. Please try again or reduce the file complexity.'
        logError(`❌ [${requestId}] Categorized as: TIMEOUT ERROR`, 'STL_IN')
      } else if (errorMessage.includes('413') || errorMessage.includes('too large')) {
        userFriendlyMessage = 'File too large: The STL file exceeds the maximum allowed size. Please reduce the file complexity.'
        logError(`❌ [${requestId}] Categorized as: FILE SIZE ERROR`, 'STL_IN')
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        userFriendlyMessage = 'Network error: Please check your internet connection and try again.'
        logError(`❌ [${requestId}] Categorized as: NETWORK ERROR`, 'STL_IN')
      } else if (errorMessage.includes('500')) {
        userFriendlyMessage = 'Server error: The backend encountered an issue. Please try again or contact support.'
        logError(`❌ [${requestId}] Categorized as: SERVER ERROR`, 'STL_IN')
      } else if (errorMessage.includes('401') || errorMessage.includes('403')) {
        userFriendlyMessage = 'Authentication error: Please log out and log back in.'
        logError(`❌ [${requestId}] Categorized as: AUTH ERROR`, 'STL_IN')
      } else if (errorMessage.includes('409')) {
        userFriendlyMessage = 'File already exists: This STL file has already been saved for this project. The existing file will be used.'
        logError(`❌ [${requestId}] Categorized as: CONFLICT ERROR (HTTP 409)`, 'STL_IN')
        // For 409 conflicts, we might want to treat this as a partial success
        setTimeout(() => {
          setSaveMessage('STL file already exists - using existing version')
        }, 2000)
      } else {
        logError(`❌ [${requestId}] Categorized as: UNKNOWN ERROR`, 'STL_IN')
      }

      setSaveMessage(userFriendlyMessage)
      logError(`❌ [${requestId}] User message: ${userFriendlyMessage}`, 'STL_IN')

      // Clear error message after 8 seconds for longer messages
      setTimeout(() => setSaveMessage(null), 8000)
    } finally {
      clearTimeout(safetyTimeout)
      setSaving(false)
      setIsSavingSTL(false)
      setUploadProgress(null)
      logInfo(`🏁 [${requestId}] Save operation cleanup completed`, 'STL_IN')
    }
    },
    { cooldown: 2000 } // 2 second cooldown for save operations
  )

  const handleOpenGirasolesView = () => {
    if (!stlData) {
      logError('Cannot open Girasoles View: No STL data loaded', 'STL_IN')
      return
    }

    try {
      // Pass through the current STL file id so GirasolesView can generate insoles
      // without requiring an extra status lookup/reload.
      const stlDataWithFileId: STLLoadResult = {
        ...stlData,
        fileId: fileId ?? stlData.fileId
      }
      tabContext.addGirasolesView(stlDataWithFileId, projectId)
      logInfo(`Opened new Girasoles View with STL data from ${fileRole.toUpperCase()} for customer: ${customerName} (Project ID: ${projectId})`, 'STL_IN')
    } catch (error) {
      logError(`Failed to open Girasoles View: ${error}`, 'STL_IN', { projectId, customerName })
    }
  }

  const handleOrderInMarketplace = () => {
    if (!projectId) {
      logError('Cannot open Marketplace: No project ID provided', 'STL_IN')
      return
    }

    try {
      tabContext.addMarketplace({
        initialTab: 'my-jobs',
        openCreateJob: true,
        draftJob: {
          projectId,
          projectName,
          patientName: customerName
        }
      })
      logInfo(`Opened Marketplace job creation flow for project: ${projectId}`, 'STL_IN')
    } catch (error) {
      logError(`Failed to open Marketplace: ${error}`, 'STL_IN', { projectId, projectName, customerName })
    }
  }

  const handleDeleteObject = async () => {
    if (!stlData) return

    const first = window.confirm('Delete current STL file from the project? This will remove the file from the database and storage.')
    if (!first) return

    const second = window.confirm('Are you sure you want to delete the STL file? This action cannot be undone.')
    if (!second) return

    setLoading(true)
    try {
      // Call backend to delete STL file
      if (projectId && projectId !== 'luis-left-foot') {
        logInfo(`Deleting STL file for project: ${projectId}`, 'STL_IN')
        await backendService.deleteSTLFile(projectId, fileRole)
        logInfo(`STL file deleted successfully from backend`, 'STL_IN')
      }

      // Dispose of geometry/material to free GPU memory
      stlData.geometry.dispose()
      const material: any = (stlData.mesh as any)?.material
      if (material && typeof material.dispose === 'function') material.dispose()

      // Clear local state
      setStlData(null)
      setOriginalUploadedFile(null)
      setError(null)
      if (fileInputRef.current) fileInputRef.current.value = ''

      logInfo(`STL object deleted successfully`, 'STL_IN')
      alert('STL file deleted successfully')
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error'
      logError(`Failed to delete STL file: ${errorMessage}`, 'STL_IN')
      setError(`Failed to delete STL file: ${errorMessage}`)
    } finally {
      setLoading(false)
    }
  }

  const handleViewerRenderError = (viewerRenderError: Error) => {
    const errorMessage = viewerRenderError.message || 'Unknown WebGL error'
    setViewerError(errorMessage)
    logError(`STL viewer fallback activated: ${errorMessage}`, 'STL_IN', {
      projectId,
      projectName,
      customerName,
      fileRole
    })
  }

  // Handle calibration point clicks from STLMesh
  const handleCalibrationPointClick = (point: { x: number; y: number; z: number }) => {
    const newPoints = { ...calibrationPoints }

    if (calibrationStep === 0) {
      newPoints.heel = { x: point.x, y: point.y, z: point.z }
      setCalibrationPoints(newPoints)
      setCalibrationStep(1)
      logInfo('Heel point captured, move to MT1', 'STL_IN')
    } else if (calibrationStep === 1) {
      newPoints.mt1 = { x: point.x, y: point.y, z: point.z }
      setCalibrationPoints(newPoints)
      setCalibrationStep(2)
      logInfo('MT1 point captured, move to MT5', 'STL_IN')
    } else if (calibrationStep === 2) {
      newPoints.mt5 = { x: point.x, y: point.y, z: point.z }
      setCalibrationPoints(newPoints)
      setCalibrationStep(3) // All points captured
      logInfo('MT5 point captured, all calibration points collected', 'STL_IN')
    }
  }

  // Calibration handlers
  const handleStartCalibration = async () => {
    if (!stlData) {
      setError('Please upload an STL file first')
      return
    }

    let calibrationFileId = await resolveExistingFileId()

    if (!calibrationFileId) {
      logInfo('No existing backend STL found, auto-saving current mesh before calibration...', 'STL_IN')
      await handleSaveSTL()
      calibrationFileId = fileIdRef.current

      // Verify save succeeded (handleSaveSTL swallows errors internally)
      if (!calibrationFileId) {
        setError('Cannot calibrate: STL must be saved first. Please save the STL and try again.')
        return
      }
    }

    // Check if calibration data already exists for this file
    setCalibrationMode(true)
    setCalibrationStep(0)
    setCalibrationPoints({})
    logInfo('Calibration mode started', 'STL_IN')

    try {
      const calData = await backendService.getCalibration(calibrationFileId)
      // Backend may return calibration_points (snake_case) or calibrationPoints (camelCase)
      const points = calData.calibrationPoints || (calData as any).calibration_points
      if (calData.calibrated && points) {
        // Convert from backend mm back to normalized scene space
        // (inverse of save conversion at saveCalibration below)
        const sf = stlData.scaleFactor || 1
        const cx = stlData.metadata.center.x
        const cy = stlData.metadata.center.y
        const cz = stlData.metadata.center.z
        setCalibrationPoints({
          heel: { x: (points.heel.x - cx) * sf, y: (points.heel.y - cy) * sf, z: (points.heel.z - cz) * sf },
          mt1:  { x: (points.mt1.x  - cx) * sf, y: (points.mt1.y  - cy) * sf, z: (points.mt1.z  - cz) * sf },
          mt5:  { x: (points.mt5.x  - cx) * sf, y: (points.mt5.y  - cy) * sf, z: (points.mt5.z  - cz) * sf },
        })
        setCalibrationStep(3) // All 3 points loaded
        logInfo('Existing calibration points loaded into wizard', 'STL_IN')
      }
    } catch {
      // No existing calibration — user starts fresh (already set to step 0)
      logInfo('No existing calibration found, starting fresh', 'STL_IN')
    }
  }

  const handleCancelCalibration = () => {
    setCalibrationMode(false)
    setCalibrationStep(0)
    setCalibrationPoints({})
    logInfo('Calibration mode cancelled', 'STL_IN')
  }

  const handleResetCalibration = () => {
    setCalibrationStep(0)
    setCalibrationPoints({})
    logInfo('Calibration points reset', 'STL_IN')
  }

  const fetchMeasurements = async (fileId: string) => {
    try {
      setMeasurementsLoading(true)
      logInfo('Fetching measurements from backend...', 'STL_IN')

      const result = await backendService.getMeasurements(fileId)

      if (result.calibrated && result.measurements) {
        setMeasurements(result.measurements)
        logInfo('Measurements loaded successfully', 'STL_IN')
      } else {
        logInfo('STL file not calibrated, measurements not available', 'STL_IN')
        setMeasurements(null)
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch measurements'
      logError(`Failed to fetch measurements: ${errorMessage}`, 'STL_IN')
      setMeasurements(null)
    } finally {
      setMeasurementsLoading(false)
    }
  }

  useEffect(() => {
    if (isOutputView || !stlData) {
      setOrientationData(null)
      return
    }

    const computed = computeLegacyOrientationData(stlData.geometry)
    setOrientationData(computed)
  }, [isOutputView, stlData, refreshKey])

  const handleConfirmCalibration = async () => {
    if (!calibrationPoints.heel || !calibrationPoints.mt1 || !calibrationPoints.mt5) {
      setError('All 3 calibration points are required')
      return
    }

    if (!stlData) {
      setError('No STL file loaded')
      return
    }

    try {
      setSaving(true)
      setIsSavingCalibration(true)
      console.log('🔵 [CALIBRATION] Starting calibration save process...')
      logInfo('🔵 [CALIBRATION] Starting calibration save process...', 'STL_IN')

      const calibrationFileId = await resolveExistingFileId()

      if (!calibrationFileId) {
        console.error('🔴 [CALIBRATION] No STL file found for this project')
        logError('🔴 [CALIBRATION] No STL file found for this project', 'STL_IN')
        throw new Error('No STL file found for this project')
      }

      console.log(`🔵 [CALIBRATION] File ID: ${calibrationFileId}`)
      logInfo(`🔵 [CALIBRATION] File ID: ${calibrationFileId}`, 'STL_IN')
      console.log('🔵 [CALIBRATION] Calibration points (normalized):', calibrationPoints)
      logInfo(`🔵 [CALIBRATION] Calibration points (normalized): ${JSON.stringify(calibrationPoints, null, 2)}`, 'STL_IN')

      // Convert calibration points from normalized scene coordinates back to original mesh coordinates
      // The normalizeGeometry() function:
      //   1) translates by -center (moves mesh to origin)
      //   2) scales by scaleFactor (scales to fit in 8 units)
      // To reverse this:
      //   1) divide by scaleFactor
      //   2) add center back
      const scaleFactor = stlData.scaleFactor || 1
      const center = stlData.metadata.center
      console.log(`🔵 [CALIBRATION] Scale factor: ${scaleFactor}`)
      console.log(`🔵 [CALIBRATION] Center offset: ${JSON.stringify(center)}`)
      logInfo(`🔵 [CALIBRATION] Scale factor: ${scaleFactor}`, 'STL_IN')
      logInfo(`🔵 [CALIBRATION] Center offset: ${JSON.stringify(center)}`, 'STL_IN')

      // Convert points back to original coordinates (divide by scaleFactor, then add center)
      const originalCalibrationPoints = {
        heel: {
          x: calibrationPoints.heel.x / scaleFactor + center.x,
          y: calibrationPoints.heel.y / scaleFactor + center.y,
          z: calibrationPoints.heel.z / scaleFactor + center.z
        },
        mt1: {
          x: calibrationPoints.mt1.x / scaleFactor + center.x,
          y: calibrationPoints.mt1.y / scaleFactor + center.y,
          z: calibrationPoints.mt1.z / scaleFactor + center.z
        },
        mt5: {
          x: calibrationPoints.mt5.x / scaleFactor + center.x,
          y: calibrationPoints.mt5.y / scaleFactor + center.y,
          z: calibrationPoints.mt5.z / scaleFactor + center.z
        }
      }

      console.log('🔵 [CALIBRATION] Calibration points (original mm):', originalCalibrationPoints)
      logInfo(`🔵 [CALIBRATION] Calibration points (original mm): ${JSON.stringify(originalCalibrationPoints, null, 2)}`, 'STL_IN')

      console.log('🔵 [CALIBRATION] Calling backend.saveCalibration()...')
      logInfo('🔵 [CALIBRATION] Calling backend.saveCalibration()...', 'STL_IN')
      const result = await backendService.saveCalibration(calibrationFileId, {
        heel: originalCalibrationPoints.heel,
        mt1: originalCalibrationPoints.mt1,
        mt5: originalCalibrationPoints.mt5,
        invert_front_back: invertFrontBackView,
        invert_top_bottom: invertTopBottomView
      })

      logInfo(`Calibration saved: ${result.message}`, 'STL_IN')
      setSaveMessage('✅ Calibration saved successfully!')

      // Fetch measurements after successful calibration
      await fetchMeasurements(calibrationFileId)

      // Exit calibration mode after successful save
      setTimeout(() => {
        setCalibrationMode(false)
        setCalibrationStep(0)
        setSaveMessage(null)
      }, 2000)

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save calibration'
      setError(`Failed to save calibration: ${errorMessage}`)
      logError(`Calibration save failed: ${errorMessage}`, 'STL_IN')
    } finally {
      setSaving(false)
      setIsSavingCalibration(false)
    }
  }

  return (
    <div className="stl-in-view" data-testid="stl-viewer">
      {/* Header */}
      <div className="stl-header">
        <div className="stl-title">
          <h2>{isOutputView ? 'STL Output View' : 'STL Input View'}</h2>
          <span className="customer-info">Customer: {customerName}</span>
        </div>
        <div className="stl-controls">
          {!isOutputView && (
            <>
              <button
                className="upload-btn"
                onClick={handleUploadClick}
                disabled={loading}
              >
                Upload STL File
              </button>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept=".stl"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
          />
        </div>
      </div>

      {/* Loading Status Modal */}
      <LoadingStatus
        isLoading={loading || saving}
        message={
          saving
            ? isSavingSTL && isSavingCalibration
              ? "Saving STL & calibration..."
              : isSavingCalibration
              ? "Saving calibration..."
              : "Saving STL..."
            : "Loading STL file..."
        }
        downloadProgress={downloadProgress || undefined}
        uploadProgress={uploadProgress || undefined}
        error={error}
        onCancel={downloadProgress ? handleCancelDownload : undefined}
        onErrorClose={() => setError(null)}
      />

      {/* Success Message */}
      {saveMessage && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#28a745',
          color: 'white',
          padding: '15px 30px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          zIndex: 10000,
          fontSize: '16px',
          fontWeight: '600'
        }}>
          {saveMessage}
        </div>
      )}

      {/* Main content area */}
      <div className="stl-content">
        {/* 3D Viewer */}
        <div className="stl-viewer" style={{ position: 'relative' }}>

          <ViewerErrorBoundary
            resetKey={`${projectId ?? 'no-project'}-${fileRole}`}
            onError={handleViewerRenderError}
            fallback={
              <div className="stl-viewer-fallback" role="status" aria-live="polite">
                <h3>3D viewer unavailable</h3>
                <p>
                  The STL file is still loaded, but this browser could not create a WebGL context.
                  You can continue with Marketplace actions and project metadata from this tab.
                </p>
              </div>
            }
          >
            <Canvas
              camera={{ position: [10, 10, 10], fov: 50 }}
              dpr={[1, 3]}
              shadows
              gl={{
                antialias: true,
                powerPreference: 'high-performance'
              }}
              style={{ background: '#f5f5f5' }}
            >
              <ambientLight intensity={0.4} />
              <directionalLight
                position={[10, 10, 5]}
                intensity={1}
                castShadow
                shadow-mapSize-width={2048}
                shadow-mapSize-height={2048}
              />

              {stlData && (
                <STLMesh
                  stlData={stlData}
                  showWireframe={showWireframe}
                  calibrationMode={calibrationMode}
                  onCalibrationPointClick={handleCalibrationPointClick}
                />
              )}
              {!isOutputView && stlData && (
                <OrientationArrows
                  stlData={stlData}
                  orientationData={orientationData}
                  invertFrontBackView={invertFrontBackView}
                  invertTopBottomView={invertTopBottomView}
                />
              )}

              {/* Calibration point markers */}
              {calibrationMode && <CalibrationMarkers calibrationPoints={calibrationPoints} />}

              <OrbitControls
                enablePan={true}
                enableZoom={true}
                enableRotate={true}
                dampingFactor={0.05}
              />

              {showStats && <Stats />}
            </Canvas>
          </ViewerErrorBoundary>
        </div>

        {/* Panel resize handle */}
        <div
          className="panel-resize-handle"
          onMouseDown={handlePanelResizeStart}
          title="Drag to resize info panel"
        />

        {/* Info Panel */}
        <div className="stl-info-panel" style={{ width: `${infoPanelWidth}px` }}>
          <h3>STL Information</h3>
          {viewerError && (
            <div className="viewer-warning" role="alert">
              3D preview unavailable: {viewerError}
            </div>
          )}
          
          {stlData ? (
            <div className="stl-metadata">
              <div className="metadata-item">
                <label>Vertices:</label>
                <span>{stlData.metadata.vertices.toLocaleString()}</span>
              </div>
              <div className="metadata-item">
                <label>Faces:</label>
                <span>{Math.floor(stlData.metadata.faces).toLocaleString()}</span>
              </div>
              {isOutputView && serverProcessingStatus?.toLowerCase().startsWith('warning') && (
                <div className="metadata-item">
                  <label>Quality Warning:</label>
                  <span>{serverProcessingStatus}</span>
                </div>
              )}
              {serverMetadata?.meshQuality && (
                <>
                  <div className="metadata-item">
                    <label>Watertight:</label>
                    <span>{serverMetadata.meshQuality.watertight ? 'Yes' : 'No'}</span>
                  </div>
                  <div className="metadata-item">
                    <label>Boundary Edges:</label>
                    <span>{serverMetadata.meshQuality.boundaryEdgeCount.toLocaleString()}</span>
                  </div>
                  {serverMetadata.meshQuality.connectedComponents != null && (
                    <div className="metadata-item">
                      <label>Components:</label>
                      <span>{serverMetadata.meshQuality.connectedComponents.toLocaleString()}</span>
                    </div>
                  )}
                </>
              )}
              {/* Size removed - use Foot Measurements panel for accurate calibrated dimensions */}
            </div>
          ) : (
            <div className="no-data">
              <p className="no-data-icon">📄</p>
              <p className="no-data-text">No STL file loaded</p>
              <p className="no-data-hint">Click <strong>Upload STL File</strong> above to import a foot scan, then <strong>Save STL</strong> to persist it to the server.</p>
            </div>
          )}

          {/* Calibration section — inside info panel so canvas stays clear */}
          {!isOutputView && stlData && (
            <div className="view-options">
              <h4>Calibration</h4>
              {!calibrationMode ? (
                <>
                  <button
                    className="process-btn calibrate-btn"
                    onClick={handleStartCalibration}
                    disabled={loading}
                    style={{
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1,
                      width: '100%',
                      marginBottom: '8px'
                    }}
                  >
                    Calibrate Foot
                  </button>
                  {measurements && (
                    <MeasurementsPanel
                      measurements={measurements}
                      loading={measurementsLoading}
                    />
                  )}
                </>
              ) : (
                <>
                  <CalibrationWizard
                    step={calibrationStep}
                    calibrationPoints={calibrationPoints}
                    onCancel={handleCancelCalibration}
                    onReset={handleResetCalibration}
                    onConfirm={handleConfirmCalibration}
                  />
                  <button
                    className="process-btn calibrate-btn"
                    onClick={handleCancelCalibration}
                    style={{
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      width: '100%'
                    }}
                  >
                    Cancel Calibration
                  </button>
                </>
              )}
            </div>
          )}

          <div className="view-options">
            <h4>View Options</h4>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showWireframe}
                onChange={(e) => setShowWireframe(e.target.checked)}
              />
              Show Wireframe
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showStats}
                onChange={(e) => setShowStats(e.target.checked)}
              />
              Show Performance Stats
            </label>
          </div>

          {!isOutputView && (
            <div className="view-options">
              <h4>Foot Orientation (STL_IN)</h4>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={invertFrontBackView}
                  onChange={(e) => setInvertFrontBackView(e.target.checked)}
                />
                Invert front/back (red arrow)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={invertTopBottomView}
                  onChange={(e) => setInvertTopBottomView(e.target.checked)}
                />
                Invert top/bottom (green arrow)
              </label>
              <p className="process-info">
                Red arrow = Front direction, Green arrow = Up direction.
              </p>
            </div>
          )}

          <div className="processing-section">
            <h4>Actions</h4>
            {!isOutputView && (
              <>
                <button
                  className="save-btn"
                  onClick={handleSaveSTL}
                  disabled={!stlData || saving || loading}
                >
                  {saving ? 'Saving...' : 'Save STL'}
                </button>
                {saveMessage && (
                  <p className={`save-message ${saveMessage.includes('Failed') ? 'error' : 'success'}`}>
                    {saveMessage}
                  </p>
                )}
              </>
            )}
            <button
              className="delete-btn"
              onClick={handleDeleteObject}
              title={`Delete ${isOutputView ? 'STL_OUT' : 'STL_IN'} file from project (database and storage)`}
              disabled={!stlData || loading}
            >
              Delete STL
            </button>
            <button
              className="process-btn"
              onClick={handleOpenGirasolesView}
              disabled={!stlData || loading}
            >
              Open Girasoles View
            </button>
            <button
              className="process-btn"
              onClick={handleOrderInMarketplace}
              disabled={!projectId}
            >
              Order in Marketplace
            </button>
            <p className="process-info">
              Open the STL file in a new Girasoles View for CAD operations.
            </p>
            <p className="process-info">
              Launch the Marketplace job form with this project preselected.
            </p>
          </div>
        </div>
      </div>

      {/* Version Display */}
      <div style={{
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        fontSize: '11px',
        color: '#999',
        fontFamily: 'monospace',
        userSelect: 'none',
        pointerEvents: 'none'
      }}>
        v{APP_VERSION}
      </div>
    </div>
  )
}
